import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Loader2, Calculator, AlertTriangle, Info, CheckCircle2, XCircle } from "lucide-react";

const TKEYS = ["turkce", "sosyal", "mat", "fen"];
const f5 = (n) => (n == null ? "—" : Number(n).toFixed(5));
const f2 = (n) => (n == null ? "—" : Number(n).toFixed(2));
const emptyRows = (keys) => Object.fromEntries(keys.map((k) => [k, { correct: "", wrong: "", cancelled: "" }]));

function NetTable({ tests, values, onChange, prefix, meta, dinExempt }) {
  const err = (t) => {
    const v = values[t.key];
    for (const [f, label] of [["correct", "Doğru"], ["wrong", "Yanlış"], ["cancelled", "İptal"]]) {
      if (v[f] === "") continue;
      if (!/^\d+$/.test(String(v[f]))) return `${label} tam sayı olmalı`;
      if (Number(v[f]) > t.total) return `${label} ${t.total} soruyu aşamaz`;
    }
    if (Number(v.correct || 0) + Number(v.wrong || 0) + Number(v.cancelled || 0) > t.total) return `Toplam ${t.total} soruyu aşamaz`;
    return "";
  };
  return (
    <table className="w-full text-sm">
      <thead><tr className="text-xs text-slate-500 border-b"><th className="text-left px-2 py-1">Test</th><th className="px-2 py-1">Soru</th><th className="px-2 py-1">Doğru</th><th className="px-2 py-1">Yanlış</th><th className="px-2 py-1" title="Kılavuz: iptal edilen sorular tüm adaylar için doğru kabul edilir">İptal</th><th className="px-2 py-1">Boş</th><th className="px-2 py-1">Net</th></tr></thead>
      <tbody>
        {tests.map((t) => {
          const e = err(t); const v = values[t.key];
          const c = Number(v.correct || 0) + Number(v.cancelled || 0), w = Number(v.wrong || 0);
          const net = e ? null : c - w / 4; const blank = e ? "—" : t.total - c - w;
          const label = t.key === "din" && dinExempt ? "Ek Felsefe Grubu (Din muafiyeti)" : t.label;
          return (
            <tr key={t.key} className="border-b border-slate-100" data-testid={`${prefix}-row-${t.key}`}>
              <td className="px-2 py-1.5 font-semibold">{label}{t.group && <div className="text-[10px] text-slate-400">{meta.ayt_groups[t.group]}</div>}</td>
              <td className="px-2 py-1.5 text-center font-mono">{t.total}</td>
              {["correct", "wrong", "cancelled"].map((f) => (
                <td key={f} className="px-1 py-1.5"><input inputMode="numeric" className={`ta-input !py-1 text-center w-16 ${e ? "!border-rose-400" : ""}`} value={v[f]} onChange={(ev) => onChange(t.key, f, ev.target.value)} placeholder="0" data-testid={`${prefix}-${t.key}-${f}`} /></td>
              ))}
              <td className="px-2 py-1.5 text-center font-mono text-slate-500">{blank}</td>
              <td className={`px-2 py-1.5 text-center font-mono font-bold ${net != null && net < 0 ? "text-rose-600" : "text-indigo-900"}`}>{net == null ? "—" : net.toFixed(2)}</td>
              {e && <td className="px-2 text-[11px] text-rose-600 whitespace-nowrap">{e}</td>}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

export default function TytCalculator() {
  const [meta, setMeta] = useState(null);
  const [years, setYears] = useState([]);
  const [year, setYear] = useState("");
  const [tyt, setTyt] = useState(emptyRows(TKEYS));
  const [ayt, setAyt] = useState({});
  const [withAyt, setWithAyt] = useState(false);
  const [dinExempt, setDinExempt] = useState(false);
  const [diploma, setDiploma] = useState("");
  const [placed, setPlaced] = useState(false);
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [target, setTarget] = useState("");
  const [sim, setSim] = useState(null);
  const [simType, setSimType] = useState("TYT");

  const simulate = async () => {
    if (!target) return toast.error("Hedef puan girin");
    setBusy(true);
    try {
      if (simType === "TYT") {
        const { data } = await api.post("/tyt/target", { year: year || undefined, target: Number(target), tests: tyt });
        setSim({ ...data, kind: "TYT" });
      } else {
        const { data } = await api.post("/yks/target", { year: year || undefined, target: Number(target), type: simType, tests: tyt, ayt_tests: ayt, din_exempt: dinExempt });
        setSim({ ...data, kind: simType });
      }
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  useEffect(() => {
    Promise.all([api.get("/tyt-params/meta"), api.get("/tyt-params")]).then(([m, p]) => {
      setMeta(m.data); setAyt(emptyRows(m.data.ayt_tests.map((t) => t.key)));
      setYears(p.data);
      const d = p.data.find((x) => x.is_default) || p.data[0];
      if (d) setYear(String(d.year));
    }).catch((e) => toast.error(apiErr(e)));
  }, []);

  const setT = (k, f, v) => { setTyt((t) => ({ ...t, [k]: { ...t[k], [f]: v } })); setRes(null); };
  const setA = (k, f, v) => { setAyt((t) => ({ ...t, [k]: { ...t[k], [f]: v } })); setRes(null); };

  const calc = async () => {
    setBusy(true);
    try {
      const body = { year: year || undefined, tests: tyt, diploma_grade: diploma || null, placed_before: placed };
      if (withAyt) {
        const { data } = await api.post("/yks/calculate", { ...body, ayt_tests: ayt, din_exempt: dinExempt });
        setRes({ yks: data, tyt: data.tyt });
      } else {
        const { data } = await api.post("/tyt/calculate", body);
        setRes({ tyt: data });
      }
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  if (!meta) return <div className="py-10 text-center text-slate-500"><Loader2 className="animate-spin inline" size={22} /></div>;
  const selected = years.find((y) => String(y.year) === year);
  const t = res?.tyt;

  return (
    <div className="grid grid-cols-1 xl:grid-cols-5 gap-5" data-testid="tyt-calculator">
      <div className="xl:col-span-3 space-y-4">
        <div className="card-ta p-5 space-y-4">
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="ta-label">Sınav Yılı (istatistik seti)</label>
              <select className="ta-input w-56" value={year} onChange={(e) => { setYear(e.target.value); setRes(null); }} data-testid="tyt-year">
                {years.length === 0 && <option value="">Parametre tanımlı değil</option>}
                {years.map((y) => <option key={y.id} value={y.year}>{y.year}{y.is_default ? " (varsayılan)" : ""}{y.complete ? "" : " — TYT seti eksik"}</option>)}
              </select>
            </div>
            {selected && !selected.complete && <div className="text-xs text-amber-700 flex items-center gap-1"><AlertTriangle size={13} /> Bu yılın TYT istatistikleri eksik; yalnızca netler hesaplanır.</div>}
            {years.length === 0 && <div className="text-xs text-amber-700 flex items-center gap-1"><AlertTriangle size={13} /> Yıl istatistikleri tanımlı değil; yalnızca netler gösterilir.</div>}
          </div>
          <div className="text-xs font-bold text-slate-700 uppercase tracking-wide">TYT</div>
          <NetTable tests={meta.tests} values={tyt} onChange={setT} prefix="tyt" meta={meta} />
        </div>

        <div className="card-ta p-5 space-y-3">
          <label className="flex items-center gap-2 text-sm font-bold text-slate-800 cursor-pointer">
            <input type="checkbox" checked={withAyt} onChange={(e) => { setWithAyt(e.target.checked); setRes(null); }} data-testid="yks-with-ayt" />
            AYT netlerini de gir → SAY / EA / SÖZ puanlarını hesapla
          </label>
          {withAyt && (
            <>
              <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
                <input type="checkbox" checked={dinExempt} onChange={(e) => { setDinExempt(e.target.checked); setRes(null); }} data-testid="yks-din-exempt" />
                Din Kültürü muafiyeti var (Sosyal Bilimler-2'de 6 ek Felsefe Grubu sorusu cevaplandı)
              </label>
              <NetTable tests={meta.ayt_tests} values={ayt} onChange={setA} prefix="ayt" meta={meta} dinExempt={dinExempt} />
            </>
          )}
        </div>

        <div className="card-ta p-5 grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
          <div>
            <label className="ta-label">Diploma Notu (isteğe bağlı, 50–100)</label>
            <input className="ta-input" value={diploma} onChange={(e) => { setDiploma(e.target.value); setRes(null); }} placeholder="Örn: 85.5" data-testid="tyt-diploma" />
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer pb-2">
            <input type="checkbox" checked={placed} onChange={(e) => { setPlaced(e.target.checked); setRes(null); }} data-testid="tyt-placed" />
            Önceki yıl bir programa yerleşti (OBP katsayısı {meta.obp_coef_placed})
          </label>
          <Button onClick={calc} disabled={busy} className="bg-indigo-900 hover:bg-indigo-800 h-10" data-testid="tyt-calc-btn">
            {busy ? <Loader2 size={15} className="animate-spin" /> : <Calculator size={15} />} <span className="ml-1">Hesapla</span>
          </Button>
          <p className="sm:col-span-3 text-[11px] text-slate-400 flex items-start gap-1"><Info size={12} className="mt-0.5 shrink-0" /> Kılavuz: Net = Doğru − Yanlış/4 (negatif korunur); iptal sorular doğru sayılır. TYT için Türkçe veya Temel Matematik ≥ {meta.min_raw} ham puan; SAY/EA/SÖZ için ayrıca ilgili iki AYT testinden en az birinde ≥ 0,5. Standart puan (ort. 50, ss 10) × Tablo 1C/1E ağırlıkları (TYT %40 + AYT %60) = ağırlıklı puan; yılın en küçük/en büyük değerleriyle 100–500 dönüşümü. Ara yuvarlama yok. OBP = diploma × 5, katkı {meta.obp_coef} (önceden yerleşen {meta.obp_coef_placed}). Başarı sırası için dağılım verisi yoksa sıralama üretilmez.</p>
        </div>
      </div>

      <div className="xl:col-span-2 space-y-3">
        <div className="card-ta p-4 space-y-2" data-testid="tyt-target-card">
          <div className="text-xs font-bold text-slate-700 uppercase tracking-wide">Hedef Puan Simülasyonu</div>
          <div className="flex rounded-lg border border-slate-200 overflow-hidden" data-testid="target-type-tabs">
            {["TYT", "SAY", "EA", "SOZ"].map((k) => (
              <button key={k} onClick={() => { setSimType(k); setSim(null); if (k !== "TYT") setWithAyt(true); }} data-testid={`target-type-${k}`}
                className={`flex-1 px-2 py-1 text-xs font-bold transition-colors ${simType === k ? "bg-indigo-900 text-white" : "text-slate-500 hover:bg-slate-100"}`}>{k === "SOZ" ? "SÖZ" : k}</button>
            ))}
          </div>
          <div className="flex gap-2">
            <input className="ta-input w-32" inputMode="decimal" placeholder="Örn: 400" value={target} onChange={(e) => { setTarget(e.target.value); setSim(null); }} data-testid="tyt-target-input" />
            <Button onClick={simulate} disabled={busy} variant="outline" className="h-10" data-testid="tyt-target-btn">Kaç net gerekir?</Button>
          </div>
          {sim && (sim.ok ? (
            sim.already ? <p className="text-xs text-emerald-700" data-testid="tyt-target-result">Mevcut netlerle tahminî {sim.kind === "SOZ" ? "SÖZ" : sim.kind} puanınız ({f2(sim.current_score)}) hedefin üzerinde.</p> : (
              <div className="text-xs text-slate-700 space-y-1" data-testid="tyt-target-result">
                <div>Mevcut tahminî {sim.kind === "SOZ" ? "SÖZ" : sim.kind} puanı <b>{f2(sim.current_score)}</b> → hedef <b>{f2(sim.target)}</b> için önerilen netler (oransal artış):</div>
                <table className="w-full text-xs"><tbody>
                  {Object.keys(sim.suggested).map((k) => <tr key={k} className="border-b border-slate-100"><td className="py-0.5">{sim.labels?.[k] || meta.tests.find((t) => t.key === k)?.label || k}</td><td className="text-right font-mono"><b>{f2(sim.suggested[k])}</b> <span className="text-emerald-700">(+{f2(sim.delta[k])})</span></td></tr>)}
                </tbody></table>
                <div className="text-[11px] text-slate-500">Önerilen netlerle tahminî puan: {f5(sim.suggested_score)}. Netler, bu puan türünde ağırlığı olan tüm testlerde mevcut netten tam nete doğru aynı oranda artırılarak bulunmuştur.</div>
              </div>
            )
          ) : <p className="text-xs text-amber-700 flex items-center gap-1" data-testid="tyt-target-result"><AlertTriangle size={12} /> {sim.reason}</p>)}
        </div>
        {!res ? <div className="card-ta p-8 text-center text-slate-400 text-sm">Sonuç burada görünecek.</div> : (
          <>
            <div className="card-ta p-5" data-testid="tyt-result">
              <div className="text-xs font-semibold text-slate-400">TYT Toplam Net</div>
              <div className={`font-display text-3xl font-extrabold ${t.total_net < 0 ? "text-rose-600" : "text-slate-900"}`} data-testid="tyt-total-net">{t.total_net.toFixed(2)}</div>
              <div className="mt-2 text-xs text-slate-500 grid grid-cols-2 gap-1">
                {TKEYS.map((k) => <div key={k}>{t.nets[k].label}: <b className={t.nets[k].net < 0 ? "text-rose-600" : "text-slate-800"}>{t.nets[k].net.toFixed(2)}</b></div>)}
              </div>
              {t.obp && <div className="mt-2 text-xs text-slate-500">OBP = {f2(t.obp.diploma_grade)} × 5 = <b>{f2(t.obp.obp)}</b> · katkı {t.obp.coef} × OBP = <b>{f5(t.obp.contribution)}</b></div>}
            </div>
            {t.score == null ? (
              <div className="card-ta p-4 border-amber-200 bg-amber-50" data-testid="tyt-no-score">
                <div className="flex items-center gap-2 text-amber-800 font-bold text-sm"><AlertTriangle size={15} /> TYT puanı hesaplanmadı</div>
                <p className="text-xs text-amber-800 mt-1">{t.reason}</p>
              </div>
            ) : (
              <div className="card-ta p-4 border-indigo-200" data-testid="tyt-score-card">
                <div className="text-xs font-semibold text-indigo-700">{t.label}</div>
                <div className="font-display text-3xl font-extrabold text-indigo-900" data-testid="tyt-score">{f5(t.score)}</div>
                <div className="text-[11px] text-slate-500">A-TYT: {f5(t.asp)}{t.params_source ? ` · Kaynak: ${t.params_source}` : ""}</div>
                {t.placement_score != null && <div className="mt-1 text-sm">Yerleştirme (TYT + OBP): <b data-testid="tyt-placement">{f5(t.placement_score)}</b></div>}
              </div>
            )}
            {res.yks && (
              <>
                <div className="card-ta p-4" data-testid="ayt-nets">
                  <div className="text-xs font-semibold text-slate-400">AYT Test Netleri</div>
                  <div className="mt-1 text-xs text-slate-600 grid grid-cols-2 gap-1">
                    {Object.entries(res.yks.group_nets).map(([g, n]) => <div key={g}>{meta.ayt_groups[g]}: <b className={n < 0 ? "text-rose-600" : ""}>{n.toFixed(2)}</b></div>)}
                  </div>
                </div>
                {Object.entries(res.yks.types).map(([code, r]) => (
                  <div key={code} className={`card-ta p-4 ${r.score != null ? "border-indigo-200" : r.eligible ? "border-amber-200 bg-amber-50" : "border-rose-200 bg-rose-50"}`} data-testid={`yks-type-${code}`}>
                    <div className="flex items-center gap-2 text-sm font-bold">
                      {r.eligible ? <CheckCircle2 size={15} className="text-emerald-600" /> : <XCircle size={15} className="text-rose-600" />}
                      {code === "SOZ" ? "SÖZ" : code} — {r.label}
                    </div>
                    {r.score != null ? (
                      <>
                        <div className="text-[11px] text-indigo-700 mt-1">{r.label}</div>
                        <div className="font-display text-3xl font-extrabold text-indigo-900" data-testid={`yks-score-${code}`}>{f5(r.score)}</div>
                        <div className="text-[11px] text-slate-500">Ağırlıklı puan A-{code}: {f5(r.asp)}</div>
                        {r.placement_score != null && <div className="mt-1 text-sm">Yerleştirme (+OBP): <b>{f5(r.placement_score)}</b></div>}
                      </>
                    ) : <p className="text-xs mt-1 text-slate-700" data-testid={`yks-reason-${code}`}>{r.reason}</p>}
                  </div>
                ))}
                <p className="text-[11px] text-slate-400">{res.yks.ranking_note}</p>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
